<script setup>
import { computed, ref, watch, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';
import TasksAPI from 'dashboard/api/tasks';
import DatePicker from 'dashboard/components-next/datetime/DatePicker.vue';
import ChipPopover from './ChipPopover.vue';
import PinAction from 'dashboard/components/widgets/conversation/header/PinAction.vue';
import { addDays, atHour, nextMonday } from './helpers/appointments';
import {
  createTaskWithExtras,
  writeTaskDraftTitle,
} from 'dashboard/components-next/workspaceV2/tasks/taskCreation';
import {
  TASK_SCOPES,
  isConversationScope,
  isOpenTask,
  isOverdueTask,
  nextTaskScope,
} from './helpers/tasksScope';

const props = defineProps({
  conversation: { type: Object, default: () => ({}) },
  open: { type: Boolean, default: false },
  compact: { type: Boolean, default: false },
  // SiteUp: so o trilho oferece fixar; no header antigo a acao nao existe.
  pinnable: { type: Boolean, default: false },
  // Fixado no topo (V3): forma expandida, contagem + proxima tarefa. Ate a
  // v970 o fixado mostrava so o icone, identico ao trilho (QA de 01/09).
  pinned: { type: Boolean, default: false },
  // SiteUp: no header desktop a Tarefa é ação (ícone leve), não pílula de estado.
  variant: {
    type: String,
    default: 'chip',
    validator: value => ['chip', 'icon'].includes(value),
  },
});

const emit = defineEmits(['toggle', 'close']);

const isHeaderIcon = computed(() => props.variant === 'icon');
const triggerClass = computed(() => {
  if (isHeaderIcon.value) {
    return [
      'relative flex shrink-0 items-center justify-center rounded-full text-n-slate-11 hover:bg-n-alpha-2 hover:text-n-slate-12 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-n-slate-11',
      props.compact ? 'size-6' : 'size-7',
    ];
  }
  return 'relative flex size-10 shrink-0 items-center justify-center rounded-full bg-n-alpha-2 text-n-slate-12 hover:bg-n-alpha-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-n-slate-11 sm:size-8';
});
const iconClass = computed(() => {
  if (isHeaderIcon.value) return props.compact ? 'size-3.5' : 'size-4';
  return 'size-[18px] shrink-0 sm:size-4';
});
const { t } = useI18n();
const router = useRouter();
const store = useStore();

const scope = ref(TASK_SCOPES.CONVERSATION);
const tasks = ref([]);
const loading = ref(false);
const loadError = ref('');
const creating = ref(false);
const draftTitle = ref('');
const customDate = ref(null);
const showCustomDate = ref(false);
const mutationError = ref('');
const completingTaskIds = ref(new Set());
// Catalogo de status da conta, que a propria listagem devolve. O chip nunca
// mais grava o literal 'pending': o status inicial sai da mesma regra do
// formulario unico (taskCreation.js).
const statuses = ref([]);

const conversationId = computed(
  () => props.conversation?.id || props.conversation?.display_id
);
const contactId = computed(() => props.conversation?.meta?.sender?.id);
const currentUserId = computed(() => store.getters.getCurrentUserID);

const openTasks = computed(() => tasks.value.filter(isOpenTask));
const pendingCount = computed(() => openTasks.value.length);

const chipLabel = computed(() => {
  // Sem pendência o tooltip fica só o rótulo: "0 tarefas" é ruído.
  if (!pendingCount.value) {
    return t('CONVERSATION.HEADER.CONTEXT_CHIPS.TASKS.TITLE');
  }
  return t(
    'CONVERSATION.HEADER.CONTEXT_CHIPS.TASKS.CHIP',
    { n: pendingCount.value },
    pendingCount.value
  );
});

// A proxima e a de vencimento mais cedo; sem vencimento vai para o fim.
const proximaTarefa = computed(
  () =>
    [...openTasks.value].sort((a, b) => {
      if (!a.due_at) return b.due_at ? 1 : 0;
      if (!b.due_at) return -1;
      return new Date(a.due_at) - new Date(b.due_at);
    })[0]
);
const pinnedLabel = computed(() => {
  if (!pendingCount.value) return chipLabel.value;
  if (props.compact || !proximaTarefa.value?.title) return chipLabel.value;
  return `${chipLabel.value} · ${proximaTarefa.value.title}`;
});

const extractTasks = payload => {
  if (Array.isArray(payload)) return payload;
  return payload?.tasks || payload?.payload || [];
};

let taskReadVersion = 0;
let taskContextVersion = 0;
onBeforeUnmount(() => {
  taskContextVersion += 1;
  taskReadVersion += 1;
});
const fetchTasks = async () => {
  taskReadVersion += 1;
  const readVersion = taskReadVersion;
  loading.value = true;
  loadError.value = '';
  try {
    const params = isConversationScope(scope.value)
      ? { conversation_display_id: conversationId.value, status_key: '' }
      : { assigned_to_user_id: currentUserId.value, status_key: '' };
    const { data } = await TasksAPI.list(params);
    if (readVersion === taskReadVersion) {
      tasks.value = extractTasks(data);
      if (Array.isArray(data?.statuses)) statuses.value = data.statuses;
    }
  } catch {
    if (readVersion === taskReadVersion) {
      loadError.value = t(
        'CONVERSATION.HEADER.CONTEXT_CHIPS.TASKS.LOAD_FAILED'
      );
    }
  } finally {
    if (readVersion === taskReadVersion) loading.value = false;
  }
};

const setScope = next => {
  scope.value = next;
};

const toggleScope = () => {
  scope.value = nextTaskScope(scope.value);
};

const finishMutationRead = () => {
  taskReadVersion += 1;
  loading.value = false;
  loadError.value = '';
};

const toggleComplete = async (task, event) => {
  if (event?.target) event.target.checked = !isOpenTask(task);
  if (completingTaskIds.value.has(task.id)) return;
  const contextVersion = taskContextVersion;
  const open = isOpenTask(task);
  mutationError.value = '';
  completingTaskIds.value = new Set(completingTaskIds.value).add(task.id);
  try {
    const { data } = open
      ? await TasksAPI.complete(task.id)
      : await TasksAPI.reopen(task.id);
    if (contextVersion !== taskContextVersion) return;
    finishMutationRead();
    tasks.value = tasks.value.map(entry =>
      entry.id === task.id ? { ...entry, ...data } : entry
    );
  } catch {
    if (contextVersion === taskContextVersion) {
      mutationError.value = t(
        'CONVERSATION.HEADER.CONTEXT_CHIPS.TASKS.MUTATION_FAILED'
      );
    }
  } finally {
    const pending = new Set(completingTaskIds.value);
    pending.delete(task.id);
    completingTaskIds.value = pending;
  }
};

const dueIso = date => atHour(date, 9).toISOString();

const createTask = async dueAt => {
  const title = draftTitle.value.trim();
  if (!title || creating.value) return;
  creating.value = true;
  mutationError.value = '';
  const contextVersion = taskContextVersion;
  try {
    const { task } = await createTaskWithExtras(
      {
        title,
        dueAt,
        link: {
          type: 'conversation',
          id: conversationId.value || null,
          contactId: contactId.value || null,
        },
      },
      { statuses: statuses.value }
    );
    if (contextVersion !== taskContextVersion) return;
    finishMutationRead();
    tasks.value = [task, ...tasks.value];
    if (draftTitle.value.trim() === title) draftTitle.value = '';
    customDate.value = null;
    showCustomDate.value = false;
  } catch {
    if (contextVersion === taskContextVersion) {
      mutationError.value = t(
        'CONVERSATION.HEADER.CONTEXT_CHIPS.TASKS.MUTATION_FAILED'
      );
    }
  } finally {
    creating.value = false;
  }
};

const createToday = () => createTask(dueIso(new Date()));
const createTomorrow = () => createTask(dueIso(addDays(new Date(), 1)));
const createMonday = () => createTask(nextMonday().toISOString());
const createCustom = iso => {
  if (!iso) return;
  const [year, month, day] = iso.split('-').map(Number);
  createTask(dueIso(new Date(year, month - 1, day)));
};

watch(customDate, iso => {
  if (iso) createCustom(iso);
});

watch(
  [
    () => conversationId.value,
    () => props.conversation?.account_id,
    currentUserId,
    scope,
  ],
  () => {
    taskContextVersion += 1;
    taskReadVersion += 1;
    tasks.value = [];
    loading.value = false;
    loadError.value = '';
    mutationError.value = '';
    if (conversationId.value || !isConversationScope(scope.value)) {
      fetchTasks();
    }
  },
  { immediate: true }
);

watch(
  () => props.open,
  open => {
    if (open) fetchTasks();
    else {
      showCustomDate.value = false;
      customDate.value = null;
    }
  }
);

defineExpose({ scope, setScope, toggleScope, TASK_SCOPES });

// Atalho pra tela cheia: o popover resolve o rápido, a tela resolve o resto.
const openTasksScreen = () => {
  router.push({ name: 'tasks' });
  emit('close');
};

// "Mais opções" abre o formulário único de nova tarefa, já ligado a esta
// conversa e com o que foi digitado aqui. É o mesmo formulário da tela de
// Tarefas, do painel do contato e do card do Kanban.
const openFullForm = () => {
  const query = { new: '1' };
  if (conversationId.value)
    query.conversation_display_id = String(conversationId.value);
  if (contactId.value) query.contact_id = String(contactId.value);
  // O titulo digitado viaja por sessionStorage, nao pela URL: titulo de tarefa
  // pode ter dado pessoal e URL fica no historico do navegador.
  writeTaskDraftTitle(draftTitle.value);
  router.push({ name: 'tasks', query });
  emit('close');
};
</script>

<template>
  <ChipPopover :open="open" @close="emit('close')">
    <template #trigger>
      <button
        v-if="pinned"
        type="button"
        data-testid="context-chip-tasks"
        data-variant="pinned"
        class="flex h-8 max-w-[260px] items-center gap-2 rounded-full border border-n-weak bg-n-solid-1 px-2.5 text-left hover:bg-n-solid-2"
        :class="{ 'bg-n-solid-2': open }"
        :aria-expanded="open"
        :aria-label="pinnedLabel"
        :title="pinnedLabel"
        @click.stop="emit('toggle')"
      >
        <span class="i-ph-check-square size-4 shrink-0" aria-hidden="true" />
        <span class="min-w-0 truncate text-[12.5px] font-bold text-n-slate-12">
          {{ pinnedLabel }}
        </span>
      </button>
      <button
        v-else
        v-tooltip.bottom="chipLabel"
        type="button"
        data-testid="context-chip-tasks"
        :data-variant="variant"
        :class="triggerClass"
        :aria-expanded="open"
        :aria-label="chipLabel"
        @click.stop="emit('toggle')"
      >
        <span
          class="i-ph-check-square shrink-0"
          :class="iconClass"
          aria-hidden="true"
        />
        <span
          v-if="pendingCount"
          class="pointer-events-none absolute -right-0.5 -top-0.5 inline-flex h-3.5 min-w-3.5 items-center justify-center rounded-full bg-n-slate-12 px-0.5 font-bold tabular-nums leading-none text-n-solid-1"
          :class="compact ? 'text-[8px]' : 'text-[9px]'"
          aria-hidden="true"
        >
          {{ pendingCount }}
        </span>
      </button>
    </template>

    <!-- No celular não há hover: o rótulo do gatilho só existe aqui dentro. -->
    <p class="mb-3 text-[14px] font-medium text-n-slate-12">
      {{ t('CONVERSATION.HEADER.CONTEXT_CHIPS.TASKS.TITLE') }}
    </p>

    <div class="flex rounded-full bg-n-alpha-2 p-0.5">
      <button
        type="button"
        data-testid="context-chip-tasks-scope-conversation"
        class="h-8 flex-1 rounded-full px-2 text-[12.5px] font-bold"
        :class="
          isConversationScope(scope)
            ? 'bg-n-solid-1 text-n-slate-12'
            : 'text-n-slate-11'
        "
        @click="setScope(TASK_SCOPES.CONVERSATION)"
      >
        {{ t('CONVERSATION.HEADER.CONTEXT_CHIPS.TASKS.SCOPE_CONVERSATION') }}
      </button>
      <button
        type="button"
        data-testid="context-chip-tasks-scope-all"
        class="h-8 flex-1 rounded-full px-2 text-[12.5px] font-bold"
        :class="
          !isConversationScope(scope)
            ? 'bg-n-solid-1 text-n-slate-12'
            : 'text-n-slate-11'
        "
        @click="setScope(TASK_SCOPES.ALL)"
      >
        {{ t('CONVERSATION.HEADER.CONTEXT_CHIPS.TASKS.SCOPE_ALL') }}
      </button>
    </div>

    <ul v-if="openTasks.length" class="mt-3 space-y-1">
      <li
        v-for="task in openTasks"
        :key="task.id"
        class="flex items-start gap-2 rounded-xl px-1 py-1"
      >
        <input
          type="checkbox"
          class="mt-1 size-4 shrink-0 rounded border-n-weak"
          :aria-label="task.title"
          :checked="!isOpenTask(task)"
          :disabled="completingTaskIds.has(task.id)"
          @change="toggleComplete(task, $event)"
        />
        <div class="min-w-0 flex-1">
          <p
            class="truncate text-[13px] font-bold"
            :class="isOverdueTask(task) ? 'text-n-ruby-11' : 'text-n-slate-12'"
          >
            {{ task.title }}
          </p>
          <p
            v-if="task.due_at"
            class="text-[12px]"
            :class="isOverdueTask(task) ? 'text-n-ruby-11' : 'text-n-slate-11'"
          >
            {{
              new Date(task.due_at).toLocaleDateString('pt-BR', {
                day: '2-digit',
                month: 'short',
              })
            }}
          </p>
        </div>
      </li>
    </ul>
    <p
      v-else-if="!loading && !loadError"
      class="mt-3 text-[13px] text-n-slate-11"
    >
      {{ t('CONVERSATION.HEADER.CONTEXT_CHIPS.TASKS.EMPTY') }}
    </p>
    <div v-if="loadError" class="mt-3 flex items-center gap-2" role="alert">
      <p class="text-[13px] text-n-ruby-11">{{ loadError }}</p>
      <button
        type="button"
        class="shrink-0 text-[13px] font-bold text-n-brand"
        @click="fetchTasks"
      >
        {{ t('CONVERSATION.HEADER.CONTEXT_CHIPS.TASKS.RETRY') }}
      </button>
    </div>
    <p
      v-if="mutationError"
      class="mt-3 text-[13px] text-n-ruby-11"
      role="alert"
    >
      {{ mutationError }}
    </p>

    <div class="mt-3">
      <input
        v-model="draftTitle"
        type="text"
        class="h-8 w-full rounded-full border border-n-weak bg-n-solid-2 px-3 text-[13px] text-n-slate-12"
        :placeholder="t('CONVERSATION.HEADER.CONTEXT_CHIPS.TASKS.PLACEHOLDER')"
        :aria-label="t('CONVERSATION.HEADER.CONTEXT_CHIPS.TASKS.PLACEHOLDER')"
        @keydown.enter.prevent="createToday"
      />
      <div class="mt-2 flex flex-wrap gap-1">
        <button
          type="button"
          class="inline-flex h-8 items-center rounded-full bg-n-alpha-2 px-2.5 text-[12px] font-bold text-n-slate-12 disabled:cursor-not-allowed disabled:opacity-40"
          :disabled="creating || !draftTitle.trim()"
          @click="createToday"
        >
          {{ t('CONVERSATION.HEADER.CONTEXT_CHIPS.TASKS.TODAY') }}
        </button>
        <button
          type="button"
          class="inline-flex h-8 items-center rounded-full bg-n-alpha-2 px-2.5 text-[12px] font-bold text-n-slate-12 disabled:cursor-not-allowed disabled:opacity-40"
          :disabled="creating || !draftTitle.trim()"
          @click="createTomorrow"
        >
          {{ t('CONVERSATION.HEADER.CONTEXT_CHIPS.TASKS.TOMORROW') }}
        </button>
        <button
          type="button"
          class="inline-flex h-8 items-center rounded-full bg-n-alpha-2 px-2.5 text-[12px] font-bold text-n-slate-12 disabled:cursor-not-allowed disabled:opacity-40"
          :disabled="creating || !draftTitle.trim()"
          @click="createMonday"
        >
          {{ t('CONVERSATION.HEADER.CONTEXT_CHIPS.TASKS.MONDAY') }}
        </button>
        <button
          type="button"
          class="inline-flex h-8 items-center rounded-full bg-n-alpha-2 px-2.5 text-[12px] font-bold text-n-slate-12 disabled:cursor-not-allowed disabled:opacity-40"
          :disabled="creating || !draftTitle.trim()"
          @click="showCustomDate = !showCustomDate"
        >
          {{ t('CONVERSATION.HEADER.CONTEXT_CHIPS.TASKS.OTHER_DATE') }}
        </button>
      </div>
      <div v-if="showCustomDate" class="mt-2">
        <DatePicker v-model="customDate" compact accent="teal" />
      </div>
    </div>

    <button
      type="button"
      data-testid="context-chip-tasks-more-options"
      class="mt-2 inline-flex h-8 w-full items-center justify-center gap-1.5 rounded-full border border-n-weak px-3 text-[13px] font-bold text-n-slate-12 hover:bg-n-alpha-2"
      @click="openFullForm"
    >
      <span
        class="i-ph-sliders-horizontal size-3.5 shrink-0"
        aria-hidden="true"
      />
      {{ t('CONVERSATION.HEADER.CONTEXT_CHIPS.TASKS.MORE_OPTIONS') }}
    </button>

    <button
      type="button"
      class="mt-2 inline-flex h-8 w-full items-center justify-center gap-1.5 rounded-full border border-n-weak px-3 text-[13px] font-bold text-n-slate-12 hover:bg-n-alpha-2"
      @click="openTasksScreen"
    >
      <span
        class="i-ph-arrow-square-out size-3.5 shrink-0"
        aria-hidden="true"
      />
      {{ t('CONVERSATION.HEADER.CONTEXT_CHIPS.TASKS.OPEN_SCREEN') }}
    </button>

    <template v-if="pinnable" #actions>
      <PinAction modulo="tasks" @done="emit('close')" />
    </template>
  </ChipPopover>
</template>
